<template>
    <div>
        <b-modal v-if="!embed" ref="taskModal" :title="getTitle" size="xl" no-fade ok-title="Save Task"
            @hidden="$emit('closed')" @ok="handleSaveTask" scrollable body-class="p-0">
            <div v-if="loading" class="text-center my-2">
                <b-spinner class="align-middle mr-2" />
                <strong>Loading...</strong>
            </div>
            <b-overlay v-else :show="processingPreSave">
                <TaskEditor ref="taskEditor" :key="activeTask.id"
                    :show-completion-question-errors="showCompletionQuestionErrors"
                    :fieldsDisabled="shouldDisableFields" v-model="activeTask" />
            </b-overlay>
            <template #modal-header="{ close }">
                <h5 class="modal-title">{{ getTitle }}</h5>
                <div class="d-flex align-items-center">
                    <b-button v-if="showWatching" disabled class="m-0" variant="outline-secondary" size="sm">
                        <i class="fas fa-eye" />
                        Watching
                    </b-button>
                    <b-button v-else-if="showWatchAction" variant="outline-primary" size="sm" class="m-0"
                        @click="addMeAsWatcher">
                        <i class="fa fa-eye" />
                        Watch
                    </b-button>
                    <button type="button" aria-label="Close" class="close" @click="close">×</button>
                </div>
            </template>
            <template #modal-footer="{ cancel, ok }">
                <div v-if="!loading" class="d-flex w-100 mt-2" style="margin-bottom: -5px;">
                    <div class="flex-fill d-flex align-items-center" style="gap: 13px;">
                        <b-button @click="cancel" class="mb-0">Cancel</b-button>

                        <b-dropdown v-if="(activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate) || showMergeAnswersAction"
                            text="Actions" variant="secondary" class="mb-0" dropup>
                            <b-dropdown-item v-if="activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate"
                                @click="showFaxModal">
                                <i class="fa fa-print fa-fw" /> Fax...
                            </b-dropdown-item>
                            <b-dropdown-item v-if="showMergeAnswersAction"
                                @click="showMergeModal">
                                <i class="fa fa-file-pdf-o fa-fw" /> Merge Answers...
                            </b-dropdown-item>
                        </b-dropdown>

                        <div v-if="activeTask.id" style="line-height: 1.1;">
                            <small>
                                <strong>Created:</strong><br>
                                {{ activeTask.createdBy }} on
                                {{ activeTask.created | formatDate("L") }}
                            </small>
                        </div>
                    </div>
                    <div class="d-flex align-items-center" style="gap: 9px;">
                        <b-button v-if="showCompleteAction" variant="success" @click="completeActiveTask" class="mb-0">
                            Complete Task
                        </b-button>
                        <b-button v-if="showSaveButton"
                            variant="primary" @click="ok" class="mb-0">
                            {{ activeTask.id ? "Save " : "Create " }}
                            {{ activeTask?.isScheduled ? "Scheduled Task" : activeTask?.isTemplate ? "Template" : "Task"
                            }}
                        </b-button>
                    </div>
                </div>
                <div v-else></div>
            </template>

        </b-modal>
        <div v-else>
            <div v-if="loading" class="text-center my-2">
                <b-spinner class="align-middle mr-2" />
                <strong>Loading...</strong>
            </div>
            <template v-else>
                <h1 class="card-title">
                    {{ activeTask?.isScheduled ? "Scheduled Task" : activeTask?.isTemplate ? "Task Template" : "Task" }}
                </h1>

                <TaskEditor ref="taskEditor" :key="activeTask.id"
                    :show-completion-question-errors="showCompletionQuestionErrors"
                    :fieldsDisabled="shouldDisableFields" v-model="activeTask" />

                <div class="actions glass-pane d-flex align-items-center" style="gap: 7px;">
                    <div class="d-flex flex-fill align-items-center" style="gap: 10px;">
                        <b-dropdown v-if="(activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate) || showMergeAnswersAction"
                            text="Actions" variant="secondary" class="mb-0" dropup>
                            <b-dropdown-item v-if="activeTask.id && !activeTask.isScheduled && !activeTask.isTemplate"
                                @click="showFaxModal">
                                <i class="fa fa-print fa-fw" /> Fax...
                            </b-dropdown-item>
                            <b-dropdown-item v-if="showMergeAnswersAction"
                                @click="showMergeModal">
                                <i class="fa fa-file-pdf-o fa-fw" /> Merge Answers...
                            </b-dropdown-item>
                        </b-dropdown>
                        <div v-if="activeTask.id" style="line-height: 1.1;">
                            <small>
                                <strong>Created:</strong><br>
                                {{ activeTask.createdBy }} on
                                {{ activeTask.created | formatDate("L") }}
                            </small>
                        </div>
                    </div>
                    <div>
                        <b-button v-if="showCompleteAction" variant="success" @click="completeActiveTask" class="mb-0">
                            Complete Task
                        </b-button>
                        <b-button v-if="showSaveButton"
                            variant="primary" @click="handleSaveTask" class="mb-0">
                            {{ activeTask.id ? "Save " : "Create " }}
                            {{ scheduled || activeTask.isScheduled ? "Scheduled " : "" }}
                            Task
                        </b-button>
                    </div>

                </div>
            </template>
        </div>

        <b-modal ref="closureQuestionsModal" title="Task Completion" size="lg" no-fade ok-variant="success"
            ok-title="Complete Task" scrollable @ok="handleCompleteQuestionsSubmit"
            :ok-disabled="loadingCompletionForm" modal-class="closure-questions-modal">
            <template #modal-footer="{ ok, cancel }">
                <div class="d-flex w-100" style="gap: 8px;">
                    <b-button @click="cancel" variant="secondary" :disabled="savingPartialAnswers || loadingCompletionForm">
                        Cancel
                    </b-button>
                    <div class="flex-fill"></div>
                    <b-button @click="handleSavePartialAnswers" variant="info" :disabled="savingPartialAnswers || loadingCompletionForm">
                        <b-spinner v-if="savingPartialAnswers" small class="mr-1" />
                        {{ savingPartialAnswers ? 'Saving...' : 'Save & Continue Later' }}
                    </b-button>
                    <b-button @click="ok" variant="success" :disabled="savingPartialAnswers || loadingCompletionForm">
                        Complete Task
                    </b-button>
                </div>
            </template>
            <TaskQuestions v-show="activeTask.completionQuestionSet?.questions?.length" ref="closureQuestions"
                :questions="activeTask.completionQuestionSet?.questions" :answers="activeClosureAnswers"
                :show-errors="showCompletionQuestionAnswerErrors" @question-input="processQuestionAnswer" />

            <hr v-if="activeTask.completionQuestionSet?.questions?.length && activeTask.completionFormImageId">

            <div v-if="loadingCompletionForm">
                <b-spinner small /> Generating Form...
            </div>
            <div v-else-if="activeTask.completionFormImageId" style="position: relative;">
                <ImageComponent :key="activeTask.completionFormImageId" style="height:calc(100vh - 300px);"
                    :imageID="activeTask.completionFormImageId" />

                <div class="text-center w-100" style="position: absolute; bottom: 0;">
                    <div class="glass-pane d-inline-block rounded m-3 px-3">
                        <div class="d-flex" style="gap: 7px;">
                            <b-button
                                :variant="activeTask.completionFormImageAccepted === true ? 'success' : 'outline-success'"
                                @click="activeTask.completionFormImageAccepted = true">
                                Accept
                            </b-button>
                            <b-button
                                :variant="activeTask.completionFormImageAccepted === false ? 'danger' : 'outline-danger'"
                                @click="activeTask.completionFormImageAccepted = false">
                                Reject
                            </b-button>
                        </div>
                    </div>
                </div>
            </div>
        </b-modal>

        <b-modal ref="faxModal" title="Fax Task Summary" no-fade>

            <b-form-group v-if="patientOptions?.length" label="Patient">
                <b-select v-model="faxData.patientId" :options="patientOptions" />
            </b-form-group>

            <b-form-group v-if="prescriptionOptions?.length" label="Prescription">
                <v-select v-model="faxData.rxId" :options="prescriptionOptions" :reduce="option => option.value">
                    <template #option="option">
                        <span v-html="option.html"></span>
                    </template>
                    <template #selected-option="option">
                        <span v-html="option.html"></span>
                    </template>
                </v-select>
            </b-form-group>

            <hr v-if="patientOptions?.length || prescriptionOptions?.length">

            <strong>Send To:</strong>

            <b-select v-model="faxData.sendTo" :options="sendToOptions" />

            <template v-if="faxData.sendTo === 0">
                <SearchComponent :key="faxData.prescriber?.id ?? 0" v-model="faxData.prescriber" :hide-label="true"
                    search-u-r-l="/Prescriber" :clearable="true" />

                <div v-if="faxData.prescriber?.id" class="d-flex align-items-center">
                    <div class="flex-fill">Fax Number: {{ faxData.prescriber.faxNumber }}</div>
                    <div>
                        <b-button size="sm" class="m-0" variant="info" @click="selectedPrescriber = faxData.prescriber">
                            Edit Prescriber
                        </b-button>
                    </div>
                </div>
                <b-check v-if="faxData.prescriber?.id && faxData.prescriber?.altFaxNumber" v-model="useAltFax" switch class="mt-2">
                    Use Alternate Fax Number ({{ faxData.prescriber.altFaxNumber }})
                </b-check>
            </template>

            <b-input v-if="faxData.sendTo === 1" v-model="faxData.faxNumber" placeholder="Enter Fax Number" />

            <div v-if="faxData.sendTo === 3 && activeTask?.storedPharmacy" class="mt-2">
                <div><strong>{{ activeTask.storedPharmacy.name }}</strong></div>
                <div>Fax Number: {{ activeTask.storedPharmacy.faxNumber }}</div>
            </div>

            <hr>

            <div class="d-flex flex-column" style="gap: 8px;">
                <b-check v-if="faxData.patientId || faxData.rxId" v-model="faxData.includePatient" switch>
                    Include Patient Information
                </b-check>
                <b-check v-if="faxData.rxId" v-model="faxData.includeRx" switch>
                    Include Prescription Information
                </b-check>
                <b-check v-if="activeTask.completionAnswers?.length" v-model="faxData.includeAnswers" switch>
                    Include Completion Answers
                </b-check>
                <b-check v-model="faxData.includeNotes" switch>
                    Include Task Notes
                </b-check>
            </div>

            <hr>

            <b-form-group label="Additional Notes to Include">
                <b-textarea rows="3" v-model="faxData.note" />
            </b-form-group>

            <hr v-if="showMergeToggle">

            <template v-if="showMergeToggle">
                <b-form-group label="Merge Document Template (optional)" class="mt-3">
                    <SearchComponent v-model="faxData.mergeDocument"
                        :hide-label="true"
                        search-u-r-l="/api/TaskCompletionAnswersMergeDocument"
                        :params="{ active: true }"
                        :clearable="true"
                        :option-label="mergeDocumentLabel"
                        prepopulate />
                </b-form-group>

                <template v-if="faxData.mergeDocument">
                    <b-check v-model="includeTaskSummary" switch class="mb-2">
                        Include Task Summary
                    </b-check>
                    <b-check v-model="faxData.linkMergedToPatient" switch>
                        Link Document to Patient Documents
                    </b-check>
                </template>
            </template>

            <template #modal-footer="{ cancel }">
                <b-button @click="cancel" :disabled="sendingFax">
                    Cancel
                </b-button>
                <b-button variant="primary" @click="sendFax" :disabled="sendingFax">
                    <b-spinner v-if="sendingFax" small class="mr-1" />
                    <i v-else class="fa fa-fax mr-1" />
                    {{ sendingFax ? 'Sending Fax...' : 'Send Fax' }}
                </b-button>
            </template>

        </b-modal>

        <b-modal :visible="!!selectedPrescriber" title="Edit Prescriber" size="xl" no-fade hide-footer
            @hidden="selectedPrescriber = null">
            <DoctorFormComponent v-if="selectedPrescriber" :prescriber-id="selectedPrescriber.id" :embedded="true"
                :button-titles="{ new: '', clear: '', save: 'Save Prescriber', cancel: 'Cancel' }"
                @close="selectedPrescriber = null" @saved="faxData.prescriber = $event; selectedPrescriber = null;" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Ref, Prop, Watch, Emit } from "vue-property-decorator";
import TaskEditor from "@/components/Tasks/TaskEditor.vue";
import { UserTask, UserTaskAnswer, UserTaskQuestionType, UserTaskStatus } from "@/models/UserTask";
import { TaskCompletionAnswersMergeDocument } from "@/models/TaskCompletionAnswersMergeDocument";
import { BModal, BvModalEvent } from "bootstrap-vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Patient } from "@/models/Patient";
import { PatientDocument } from "@/models/PatientDocument";
import { Prescription } from "@/models/Prescription";
import moment from "moment";
import TaskQuestions from "./TaskQuestions.vue";
import SearchComponent from "../SearchComponent.vue";
import { Prescriber } from "@/models/Prescriber";
import DoctorFormComponent from "../Doctor/DoctorFormComponent.vue";
import Axios from "axios";
import ImageComponent from "../Prescription/ImageComponent.vue";
import TextUtils from "@/util/TextUtils";
import { getUserSetting, saveUserSetting } from "@/services/UserSettingsService";

const FaxIncludeTaskSummaryKey = "fax-task-include-summary";
const FaxLinkToPatientDocsKey = "fax-task-link-to-patient-docs";

@Component({
    components: {
        TaskEditor,
        TaskQuestions,
        SearchComponent,
        DoctorFormComponent,
        ImageComponent
    },
})
export default class TaskDetails extends Vue {

    @Prop({ default: 0 }) protected id!: number | string;
    @Prop() protected patientId?: number | string;
    @Prop() protected prescriptionId?: string;
    @Prop({ default: false }) protected embed!: boolean;
    @Prop({ default: false }) protected scheduled!: boolean;
    @Prop({ default: false }) protected template!: boolean;
    @Prop({ default: () => [] }) protected patientDocuments!: PatientDocument[];

    @Ref("taskModal") protected taskModal?: BModal;
    @Ref("faxModal") protected faxModal?: BModal;
    @Ref("closureQuestionsModal") protected closureQuestionsModal?: BModal;
    @Ref("taskEditor") protected taskEditor?: TaskEditor;
    @Ref("closureQuestions") protected closureQuestions?: TaskQuestions;

    protected loading = false;

    private activeTask: UserTask = new UserTask();
    private initialTaskStatus?: number;

    private isCompleting = false;
    private showCompletionQuestionErrors = false;
    private showCompletionQuestionAnswerErrors = false;
    private activeClosureAnswers: UserTaskAnswer[] = [];
    private activeMergeDocuments: TaskCompletionAnswersMergeDocument[] = [];

    UserTaskStatus = UserTaskStatus;

    mounted() {
        this.getTask();
        this.loadActiveMergeDocuments();
        this.taskModal?.show();

        if (!this.id || this.id === "0") {
            if (this.patientId) {
                this.addDefaultPatient(this.patientId);
            }

            const queryPatientId = this.$route.query.patientid || this.$route.query.patientId;
            if (queryPatientId) {
                this.addDefaultPatient(queryPatientId as string);
            }

            if (this.prescriptionId) {
                this.addDefaultPrescription(this.prescriptionId);
            }

            const queryPrescriptionId = this.$route.query.prescriptionid || this.$route.query.prescriptionId;
            if (queryPrescriptionId) {
                this.addDefaultPrescription(queryPrescriptionId as string);
            }
        }
    }

    @Watch("id")
    onTaskIdChange() {
        this.getTask();
    }

    @Watch("activeTask.status")
    handleStatusChange(newStatus: number) {
        this.isCompleting = (newStatus === 2);
    }

    async addDefaultPatient(patientId: string | number) {
        try {
            const response = await this.$http.get<Patient>(`/Patient/${patientId}`);
            this.taskEditor?.addPatient(Object.assign(new Patient(), response.data));
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    async addDefaultPrescription(prescriptionId: string) {
        try {
            const prescriptionResponse = await this.$http.get<Prescription>(`/Prescription/${prescriptionId}`);
            const prescription = Object.assign(new Prescription(), prescriptionResponse.data);

            const prescriberResponse = await this.$http.get<Prescriber>(`/Prescriber/${prescription.prescriberID}`);
            prescription.prescriber = prescriberResponse.data;

            this.taskEditor?.addPrescription(prescription);
        } catch (err) {
            console.error(err);
            this.$notification(NotificationOptions.error(err));
        }
    }

    @Emit()
    async getTask() {
        if (!Number(this.id)) {
            this.activeTask = Object.assign(new UserTask(), {
                isScheduled: this.scheduled,
                isTemplate: this.template,
                startDate: moment.utc([moment().year(), moment().month(), moment().date()]),
                dueDate: moment.utc([moment().year(), moment().month(), moment().date()]),
                expiredOnDate: null,
                enabled: true,
                repeatMode: 0,
                completionQuestionSet: {
                    questions: []
                },
                completionAnswers: [],
                limitToTeamIds: [],
                limitToProgramIds: [],
                limitToRxStatuses: [],
                blockCheckout: false,
                patientDocuments: this.patientDocuments.length ? this.patientDocuments : []
            });
            this.initialTaskStatus = this.activeTask.status; // Set for new tasks too
            return;
        }

        this.loading = true;

        try {
            const response = await this.$http.get(`/task/${this.id}`);
            this.activeTask = new UserTask(response.data);
            this.initialTaskStatus = response.data.status;
            this.activeClosureAnswers = this.activeTask.completionAnswers ?? [];
            return this.activeTask;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.loading = false;
        }
    }

    validateTask() {
        this.showCompletionQuestionErrors = false;

        if (!this.activeTask.title?.trim()) {
            this.$notification(NotificationOptions.error("You must provide a title for this task."));
            return false;
        }

        if (!this.activeTask.startDate) {
            this.$notification(NotificationOptions.error("You must provide a start date for this task."));
            return false;
        }

        if (!this.activeTask.dueDate) {
            this.$notification(NotificationOptions.error("You must provide a due date for this task."));
            return false;
        }

        if (this.taskEditor?.requireTaskAssignment && (!this.activeTask.assignments || this.activeTask.assignments.length === 0)) {
            this.$notification(NotificationOptions.error("A task assignment is required. Please assign this task before saving."));
            return false;
        }

        const isScheduled = this.activeTask.isScheduled;
        const repeatSchedule = this.activeTask.repeatSchedule;

        if (isScheduled && !repeatSchedule) {
            this.$notification(NotificationOptions.error("You must select a schedule for this scheduled task."));
            return false;
        }

        if (isScheduled && this.activeTask.repeatMode === 0 && (repeatSchedule === "0" || repeatSchedule?.includes("-"))) {
            this.$notification(NotificationOptions.error("You must enter a number of days greater than 0."));
            return false;
        }

        const isTemplate = this.activeTask.isTemplate;
        const hasCompletionQuestions = !!this.activeTask.completionQuestionSet?.questions?.length;
        const canEditCompletionQuestions = isScheduled || isTemplate || !this.activeTask.id;

        if (canEditCompletionQuestions && hasCompletionQuestions) {
            const questionsHaveErrors = this.activeTask.completionQuestionSet?.questions.some(q => {
                if (!q.text?.trim()) return true;
                if (![UserTaskQuestionType.multipleChoiceMultiple, UserTaskQuestionType.multipleChoiceSingle].includes(q.typeId)) return false;
                if (q.options.some(o => !o.text?.trim())) return true;
                const uniqueTexts = new Set(q.options.map(o => o.text?.trim()));
                if (uniqueTexts.size !== q.options.length) return true;
                return false;
            });
            if (questionsHaveErrors) {
                this.$nextTick(() => {
                    this.showCompletionQuestionErrors = true;
                });
                this.$notification(NotificationOptions.error("Some completion questions have errors."));
                return false;
            }
        }

        return true;
    }

    protected loadingCompletionForm = false;
    protected processingPreSave = false;
    protected savingPartialAnswers = false;

    async handleSaveTask(e?: any) {
        e?.preventDefault();

        if (!this.validateTask()) {
            return;
        }

        // Save any pending note content before saving the task
        await this.taskEditor?.savePendingNote();

        if (!this.isCompleting) {
            await this.saveTask();
            return;
        }

        // Templates and scheduled tasks should never show completion modal
        if (this.activeTask.isTemplate || this.activeTask.isScheduled) {
            await this.saveTask();
            return;
        }

        try {
            this.processingPreSave = true;

            const hasClosureQuestions = !!this.activeTask.completionQuestionSet?.questions?.length;
            const hasCompletionForm = !!this.activeTask.taskCompletionFormId;

            let shouldShowCompletionModal = hasClosureQuestions;

            if (hasCompletionForm) {
                this.activeTask.completionFormImageId = null;
                this.activeTask.completionFormImageAccepted = null;

                await this.generateCompletionForm();

                if (this.activeTask.completionFormImageId) {
                    shouldShowCompletionModal = true;
                }
            }

            if (shouldShowCompletionModal) {
                this.closureQuestionsModal?.show();
                this.taskEditor?.resetActiveTab();

                return;
            }

            await this.saveTask();
        } catch (err) {
            console.warn(err);
        } finally {
            this.processingPreSave = false;
        }
    }

    async generateCompletionForm() {
        try {
            this.loadingCompletionForm = true;
            const response = await Axios.post<number | null>(`/task/generate-completion-form/${this.activeTask.id}`);
            this.activeTask.completionFormImageId = !response.data ? null : response.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loadingCompletionForm = false;
        }
    }

    async saveTask() {
        try {
            // Clean up empty taskCompletionForm object
            if (this.activeTask.taskCompletionForm && !this.activeTask.taskCompletionForm.id) {
                this.activeTask.taskCompletionForm = null;
            }

            const response = await this.$http.post<UserTask>("/task/", this.activeTask);
            this.activeTask = response.data;
            this.$emit("saved", response.data);
            this.$notification(NotificationOptions.success(response.data.isScheduled ? "Scheduled Task saved" : response.data.isTemplate ? "Template saved" : "Task saved"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async updateWatchers() {
        if (!this.activeTask.id) return;
        try {
            const response = await this.$http.post<UserTask>("/task/watch", this.activeTask);
            this.$emit("updated", response.data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    async completeActiveTask() {
        this.activeTask.status = UserTaskStatus.complete;
        this.$nextTick(() => {
            this.handleSaveTask();
        })
    }

    get getTitle() {
        if (this.loading || !this.activeTask) return "";

        if (this.activeTask.isScheduled) {
            return this.activeTask?.id ? 'Edit Scheduled Task' : 'New Scheduled Task';
        }

        if (this.activeTask.isTemplate) {
            return this.activeTask?.id ? 'Edit Task Template' : 'New Task Template';
        }

        return this.activeTask.id ? 'Edit Task' : 'New Task';
    }

    get showCompleteAction() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            this.activeTask?.id &&
            this.activeTask?.status !== UserTaskStatus.complete;
    }

    get showMergeAnswersAction() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            this.activeTask?.id &&
            this.activeTask?.status === UserTaskStatus.complete &&
            this.activeTask?.taskCompletionQuestionSetId &&
            this.activeMergeDocuments.length > 0 &&
            (this.$permissions?.some(p => p.permissionCodeName === "Admin/Task Completion Answers Generate") ?? false);
    }

    get showWatching() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            this.activeTask?.watchers?.find(w => w.userName?.toLocaleLowerCase() === this.$user.userName.toLowerCase()) &&
            (this.initialTaskStatus !== UserTaskStatus.complete || this.hasModifyCompletedTasksPermission);
    }

    get showWatchAction() {
        return !this.activeTask?.isScheduled &&
            !this.activeTask?.isTemplate &&
            !this.activeTask?.watchers?.find(w => w.userName?.toLocaleLowerCase() === this.$user.userName.toLowerCase()) &&
            (this.initialTaskStatus !== UserTaskStatus.complete || this.hasModifyCompletedTasksPermission);
    }

    get hasModifyCompletedTasksPermission() {
        return this.$permissions?.some(p => p.permissionCodeName === "Processing/Modify Completed Tasks") ?? false;
    }

    get shouldDisableFields() {
        // If we're still loading or haven't set initial status yet, don't disable
        if (this.loading || this.initialTaskStatus === undefined) {
            return false;
        }

        return this.initialTaskStatus === UserTaskStatus.complete &&
               !this.hasModifyCompletedTasksPermission;
    }

    get showSaveButton() {
        return this.initialTaskStatus !== UserTaskStatus.complete || this.hasModifyCompletedTasksPermission;
    }

    addMeAsWatcher() {
        this.taskEditor?.addMeAsWatcher();
        this.$nextTick(() => {
            this.updateWatchers();
        });
    }

    processQuestionAnswer() {
        this.activeTask.completionAnswers = this.closureQuestions?.cleanAnswers();
    }

    async handleCompleteQuestionsSubmit(e: any) {
        e.preventDefault();

        this.showCompletionQuestionAnswerErrors = false;

        if (this.closureQuestions?.answersHaveErrors()) {
            this.$nextTick(() => {
                this.showCompletionQuestionAnswerErrors = true;
            });

            this.$notification(NotificationOptions.error("There are errors on the completion questions."));
            return;
        }

        if (this.activeTask.completionFormImageId && this.activeTask.completionFormImageAccepted === null) {
            this.$notification(NotificationOptions.error("You must accept or reject the form."));
            return;
        }

        await this.saveTask();
        this.closureQuestionsModal?.hide();
    }

    async handleSavePartialAnswers() {
        try {
            this.savingPartialAnswers = true;

            this.processQuestionAnswer();

            this.activeTask.status = this.initialTaskStatus ?? UserTaskStatus.inProgress;

            if (this.activeTask.taskCompletionForm && !this.activeTask.taskCompletionForm.id) {
                this.activeTask.taskCompletionForm = undefined;
            }

            // Save the task
            const response = await this.$http.post<UserTask>("/task/", this.activeTask);
            this.activeTask = response.data;
            this.$emit("partial-saved", response.data);

            this.closureQuestionsModal?.hide();

            this.$notification(NotificationOptions.success("Partial answers saved."));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.savingPartialAnswers = false;
        }
    }

    protected sendingFax = false;
    protected useAltFax = false;

    protected faxData = {
        patientId: 0,
        rxId: "",
        prescriber: new Prescriber(),
        includePatient: true,
        includeRx: true,
        includeNotes: false,
        includeAnswers: true,
        note: "",
        sendTo: 0,
        faxNumber: "",
        mergeDocument: null,
        mergeMode: "attach",
        linkMergedToPatient: true
    };

    showFaxModal() {
        this.faxData.rxId = "";
        this.faxData.patientId = 0;
        this.faxData.mergeDocument = null;
        this.faxData.mergeMode = "attach";
        this.faxData.linkMergedToPatient = true;
        this.useAltFax = false;

        const savedIncludeSummary = getUserSetting(FaxIncludeTaskSummaryKey);
        if (savedIncludeSummary !== null) {
            this.faxData.mergeMode = JSON.parse(savedIncludeSummary) ? "attach" : "replace";
        }

        const savedLinkToPatient = getUserSetting(FaxLinkToPatientDocsKey);
        if (savedLinkToPatient !== null) {
            this.faxData.linkMergedToPatient = JSON.parse(savedLinkToPatient);
        }

        if (this.patientOptions?.length) {
            this.faxData.patientId = this.patientOptions[0].value;
        }

        if (this.prescriptionOptions?.length) {
            this.faxData.rxId = this.prescriptionOptions[0].value;
        }

        //this.faxDataRxChanged();

        this.faxModal?.show();
    }

    mergeDocumentLabel(doc: TaskCompletionAnswersMergeDocument) {
        return doc?.name || "";
    }

    showMergeModal() {
        // Emit event to parent to show merge document selection modal
        this.$emit('show-merge-modal', {
            taskId: this.activeTask.id,
            documents: this.activeMergeDocuments
        });
    }

    async loadActiveMergeDocuments() {
        try {
            const response = await this.$http.get('/api/Task/active-merge-documents');
            this.activeMergeDocuments = response.data;
        } catch (error) {
            console.error('Error loading active merge documents:', error);
            // Fail silently to not interrupt task functionality
        }
    }

    async sendFax(e: BvModalEvent) {
        e.preventDefault();

        // Prevent double-submission if already sending
        if (this.sendingFax) return;

        try {
            this.sendingFax = true;

            const data = {
                ...this.faxData,
                taskId: this.activeTask.id,
                mergeDocumentId: this.faxData.mergeDocument?.id ?? null,
                mergeMode: this.faxData.mergeDocument ? this.faxData.mergeMode : null,
                linkMergedToPatient: this.faxData.mergeDocument ? this.faxData.linkMergedToPatient : false,
                faxNumber: this.faxData.faxNumber ? TextUtils.formatFaxNumber(this.faxData.faxNumber) : this.faxData.faxNumber,
                useAltFax: this.useAltFax
            };
            var resp = await this.$http.post("/task/fax", data);

            this.$notification(NotificationOptions.success(resp.data));
            this.faxModal?.hide();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.sendingFax = false;
        }
    }

    get patientOptions() {
        const patients: any[] = [];

        this.activeTask?.patients?.forEach(p => {
            patients.push({ value: p.id, text: `${p.lastName}, ${p.firstName}` });
        })

        return patients;
    }

    get prescriptionOptions() {
        if (this.faxData.patientId) {
            return this.activeTask?.prescriptions?.filter(rx => rx.patientID === this.faxData.patientId)
                .map(rx => new Prescription(rx))
                .map(rx => ({ value: rx.rxID, label: rx.rxID, html: rx.toString() }));
        }
        return this.activeTask?.prescriptions?.map(rx => new Prescription(rx))
            .map(rx => ({ value: rx.rxID, label: rx.rxID, html: rx.toString() }));
    }

    @Watch("faxData.rxId")
    faxDataRxChanged() {
        const rx = this.activeTask?.prescriptions?.find(rx => new Prescription(rx).rxID === this.faxData.rxId);

        if (!rx) {
            this.faxData.prescriber = new Prescriber();
        } else {
            this.faxData.prescriber = Object.assign(new Prescriber(), { id: rx.prescriberID });
        }
        this.useAltFax = false;
    }

    @Watch("faxData.prescriber")
    faxDataPrescriberChanged() {
        this.useAltFax = false;
    }

    get sendToOptions() {
        const options = [{ value: 0, text: 'Prescriber' }, { value: 1, text: 'Other Fax Number' }];

        if (this.activeTask?.storedPharmacy?.faxNumber)
            options.push({ value: 3, text: 'Stored Pharmacy' });

        if (this.patientOptions?.length || this.prescriptionOptions?.length)
            options.push({ value: 2, text: 'No Fax' });

        return options;
    }

    get showMergeToggle() {
        // Only show merge toggle if:
        // 1. Task is completed
        // 2. Task has completion answers
        // 3. User has permission to merge answers
        return this.activeTask?.status === UserTaskStatus.complete &&
            this.activeTask?.completionAnswers?.length > 0 &&
            (this.$permissions?.some(p => p.permissionCodeName === "Admin/Task Completion Answers Generate") ?? false);
    }

    get includeTaskSummary() {
        return this.faxData.mergeMode === "attach";
    }

    set includeTaskSummary(value: boolean) {
        this.faxData.mergeMode = value ? "attach" : "replace";
        saveUserSetting(FaxIncludeTaskSummaryKey, JSON.stringify(value));
    }

    @Watch("faxData.linkMergedToPatient")
    onLinkMergedToPatientChange(value: boolean) {
        saveUserSetting(FaxLinkToPatientDocsKey, JSON.stringify(value));
    }

    protected selectedPrescriber: Prescriber | null = null;
}

</script>

<style scoped lang="scss">
.actions {
    position: sticky;
    bottom: 0;
    padding: 6px;
    z-index: 400;
}

::v-deep(.modal-content .modal-header) {
    flex-shrink: inherit;
    padding-top: 15px;
    padding-bottom: 0;
}

::v-deep(.modal-content .modal-header button) {
    position: inherit;
}

::v-deep(.modal-content .modal-header button:disabled) {
    border-color: transparent;
    opacity: .9;
    padding-left: 0;
    padding-right: 0;
}
</style>
